(() => {
  if (window.__collectionLoadMoreInitialized) return;
  window.__collectionLoadMoreInitialized = true;

  const STORAGE_KEY = 'otto:collection-scroll';
  const MAX_RESTORE_PAGES = 3;

  const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  const fetchPage = async (url) => {
    const controller = new AbortController();
    let timeout;
    try {
      return await Promise.race([
        fetch(url, { signal: controller.signal }).then(async response => {
          if (!response.ok) throw new Error('Load more failed');
          return parseHtml(await response.text());
        }),
        new Promise((_, reject) => {
          timeout = setTimeout(() => { controller.abort(); reject(new Error('Load more timeout')); }, 10000);
        }),
      ]);
    } finally { clearTimeout(timeout); }
  };

  const fillTemplate = (template, vars) =>
    Object.keys(vars).reduce((text, key) => text.replaceAll(`__${key.toUpperCase()}__`, String(vars[key])), template || '');

  // The locale files carry both plural forms; the count is only known here.
  const pluralTemplate = (root, name, count) =>
    count === 1 ? root.dataset[`${name}One`] : root.dataset[`${name}Other`];

  const gridFor = (root) => {
    const scope = root.closest('#collection-with-facets, [data-element="collection"], [data-element="predictive-search"]') || root.parentElement;
    return scope?.querySelector('[data-product-grid]');
  };

  const publicUrlFrom = (url) => {
    const next = new URL(url, window.location.origin);
    next.searchParams.delete('section_id');
    return `${next.pathname}${next.search}`;
  };

  const storageUrlKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  };

  const saveScrollState = (root) => {
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          url: storageUrlKey(),
          page: Number(root.dataset.page) || 1,
          scrollY: window.scrollY,
          itemCount: gridFor(root)?.children.length || 0,
        })
      );
    } catch (error) {
      /* ignore quota / private mode */
    }
  };

  const readScrollState = () => {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const setButtonLabel = (root, loaded, total) => {
    const label = root.querySelector('[data-load-more-label]');
    if (label) {
      label.textContent = fillTemplate(root.dataset.labelTemplate, { loaded, total });
    }
  };

  const showComplete = (root) => {
    const button = root.querySelector('[data-load-more-button]');
    const total = gridFor(root)?.children.length || 0;
    let complete = root.querySelector('[data-load-more-complete]');
    if (button || complete) {
      complete ||= document.createElement('p');
      complete.className = 'collection-load-more__complete';
      complete.setAttribute('data-load-more-complete', '');
      const startPage = Number(root.dataset.startPage) || 1;
      const template = startPage > 1 ? root.dataset.partialLabel : pluralTemplate(root, 'seenAll', total);
      complete.textContent = fillTemplate(template, { count: total, page: startPage });
      if (button) button.replaceWith(complete);
    }
    root.dataset.nextUrl = '';
  };

  const announce = (root, count) => {
    const live = root.querySelector('[data-load-more-live]');
    if (!live) return;
    live.textContent = fillTemplate(pluralTemplate(root, 'announced', count), { count });
  };

  const focusFirstCard = (node) => {
    const card = node.querySelector('product-card, a, [tabindex]') || node;
    if (!(card instanceof HTMLElement)) return;
    card.tabIndex = -1;
    card.focus({ preventScroll: true });
  };

  const sectionUrl = (root, nextUrl) => {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    return url;
  };

  const appendPage = async (root, { announceResult = true, focusResult = true } = {}) => {
    const nextUrl = root.dataset.nextUrl;
    if (!nextUrl || root.dataset.loading === 'true') return false;

    const button = root.querySelector('[data-load-more-button]');
    const grid = gridFor(root);
    if (!grid) return false;

    const previousLabel = button?.querySelector('[data-load-more-label]')?.textContent || '';
    const labelEl = button?.querySelector('[data-load-more-label]');
    root.dataset.loading = 'true';
    if (button) {
      button.disabled = true;
      button.classList.remove('is-error');
      if (labelEl) labelEl.textContent = root.dataset.loadingLabel || '…';
    }

    try {
      let cursor = nextUrl;
      let fetchedRoot;
      let newItems;
      const visited = new Set();
      while (cursor) {
        if (visited.has(cursor) || visited.size >= 5) throw new Error('Pagination did not progress');
        visited.add(cursor);
        const doc = await fetchPage(sectionUrl(root, cursor).toString());
        const fetchedGrid = doc.querySelector('[data-product-grid]');
        fetchedRoot = doc.querySelector('[data-load-more-root]');
        if (!fetchedGrid || !fetchedRoot) throw new Error('Missing grid');
        newItems = Array.from(fetchedGrid.children);
        if (newItems.length || !fetchedRoot.dataset.nextUrl) break;
        cursor = fetchedRoot.dataset.nextUrl;
        root.dataset.nextUrl = cursor;
      }

      const firstNew = newItems[0];
      grid.append(...newItems);
      if (grid.children.length) root.parentElement?.querySelector('.collection-empty-state')?.remove();

      const loaded = grid.children.length;
      const total = Number(fetchedRoot?.dataset.total || root.dataset.total) || loaded;
      const fetchedNext = fetchedRoot?.dataset.nextUrl || '';

      root.dataset.loaded = String(Math.min(loaded, total));
      root.dataset.total = String(total);
      root.dataset.page = fetchedRoot?.dataset.page || String((Number(root.dataset.page) || 1) + 1);
      root.dataset.nextUrl = fetchedNext;

      history.replaceState({ ...(history.state || {}), loadMore: true }, '', publicUrlFrom(cursor));

      if (fetchedNext) {
        setButtonLabel(root, Math.min(loaded, total), total);
        if (button) button.disabled = false;
      } else {
        showComplete(root);
      }

      if (announceResult) announce(root, newItems.length);
      if (focusResult && firstNew) focusFirstCard(firstNew);
      return true;
    } catch (error) {
      window.__ottoReportUIError?.('load_more');
      root.classList.remove('is-enhanced');
      const pagination = root.querySelector('[data-pagination]');
      if (pagination) pagination.hidden = false;
      if (button) {
        button.disabled = false;
        button.classList.add('is-error');
        if (labelEl) labelEl.textContent = root.dataset.errorLabel || previousLabel;
      }
      return false;
    } finally {
      root.dataset.loading = 'false';
    }
  };

  const enhance = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.enhanced === 'true') return;
    root.dataset.enhanced = 'true';
    root.classList.add('is-enhanced');

    const loadMore = root.querySelector('.collection-load-more');
    if (loadMore) loadMore.hidden = false;

    const pagination = root.querySelector('[data-pagination]');
    // Hiding visually while retaining focusable pagination links creates an
    // inaccessible keyboard tab stop. The native hidden state removes it from
    // both the visual and accessibility trees, and is restored on an error.
    if (pagination) pagination.hidden = true;
    const loaded = gridFor(root)?.children.length || 0;
    root.dataset.loaded = String(loaded);
    setButtonLabel(root, loaded, Number(root.dataset.total) || 0);
    if (!root.dataset.nextUrl) showComplete(root);
    if (root.dataset.visibleTotal !== undefined && root.dataset.countLabel) {
      document.querySelectorAll('[data-product-count-label]').forEach(node => {
        node.textContent = root.dataset.countLabel;
      });
    }
  };

  const enhanceAll = () => {
    document.querySelectorAll('[data-load-more-root]').forEach(enhance);
  };

  const restoreScroll = async () => {
    const state = readScrollState();
    if (!state || state.url !== storageUrlKey()) return;

    const root = document.querySelector('[data-load-more-root]');
    if (!root) return;

    const grid = gridFor(root);
    if (!grid) return;

    const targetPage = Math.min(Number(state.page) || 1, MAX_RESTORE_PAGES);
    const currentPage = Number(root.dataset.page) || 1;

    if (targetPage > 1 && (currentPage !== 1 || grid.children.length < (Number(state.itemCount) || 0))) {
      const pageOne = new URL(window.location.href);
      pageOne.searchParams.delete('page');
      pageOne.searchParams.set('section_id', root.dataset.sectionId);

      try {
        const doc = await fetchPage(pageOne.toString());
        if (doc) {
          const pageOneGrid = doc.querySelector('[data-product-grid]');
          const pageOneRoot = doc.querySelector('[data-load-more-root]');
          if (pageOneGrid) {
            grid.replaceChildren(...Array.from(pageOneGrid.children));
            root.dataset.startPage = '1';
            root.querySelector('[data-first-page]')?.remove();
            root.dataset.page = pageOneRoot?.dataset.page || '1';
            root.dataset.nextUrl = pageOneRoot?.dataset.nextUrl || '';
            root.dataset.total = pageOneRoot?.dataset.total || root.dataset.total;
            root.dataset.loaded = String(grid.children.length);
            const button = root.querySelector('[data-load-more-button]');
            if (button && root.dataset.nextUrl) {
              button.disabled = false;
              button.classList.remove('is-error');
              setButtonLabel(root, grid.children.length, Number(root.dataset.total) || 0);
            }
          }
        }
      } catch (error) {
        return;
      }

      let page = 1;
      while (page < targetPage && root.dataset.nextUrl) {
        const ok = await appendPage(root, { announceResult: false, focusResult: false });
        if (!ok) break;
        page = Number(root.dataset.page) || page + 1;
      }
    }

    const scrollY = Number(state.scrollY);
    if (Number.isFinite(scrollY)) {
      window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-load-more-button]');
    if (button) {
      const root = button.closest('[data-load-more-root]');
      if (!root) return;
      event.preventDefault();
      appendPage(root);
      return;
    }

    const productLink = target.closest('[data-product-grid] a[href]');
    if (productLink) {
      const root = document.querySelector('[data-load-more-root]');
      if (root) saveScrollState(root);
    }
  });

  document.addEventListener('collection:updated', enhanceAll);
  document.addEventListener('shopify:section:load', enhanceAll);

  const init = () => {
    enhanceAll();
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav?.type === 'back_forward') {
      restoreScroll();
    }
  };

  window.addEventListener('pageshow', (event) => {
    enhanceAll();
    if (event.persisted) {
      const state = readScrollState();
      if (state && state.url === storageUrlKey()) {
        window.scrollTo(0, Number(state.scrollY) || 0);
      }
      return;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
